import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { LANDING_PAGE_EMAIL_INVITATION_ROUTE } from '@mwe/constants';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AppStorageService,
  AuthService,
  EnvironmentService,
  NavigationLogic,
  ProfileService,
  StaticContentService,
  ToastService,
} from '@mwe/services';
import { FrontpageAlertData, FrontpageContentData, FrontpageData } from '@mwe/models';
import { ButtonComponent, HeaderLogoComponent, InfoboxComponent, InlineNotificationComponent } from '@mwe/ui/components';
import { NgStyle } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { SafeHtmlPipe } from '@mwe/ui/pipes';

@Component({
  selector: 'mwe-frontpage-base',
  templateUrl: './base-frontpage.component.html',
  styleUrls: [],
  imports: [
    HeaderLogoComponent,
    TranslateDirective,
    ButtonComponent,
    NgStyle,
    TranslatePipe,
    SafeHtmlPipe,
    InlineNotificationComponent,
    InfoboxComponent,
  ],
})
export class BaseFrontpageComponent implements OnInit {
  defaultContent = input.required<FrontpageContentData>();

  isLoaded = signal<boolean>(false);
  content = signal<FrontpageContentData>(null);
  isEmailInvitationLandingPage = signal<boolean>(false);

  authServerAlertData = computed<FrontpageAlertData>(() => this.appStorage.getAuthServerFailure());
  showAuthServerAlert = computed<boolean>(() => this.authServerAlertData() && Object.keys(this.authServerAlertData()).length !== 0);

  titleBigLabelKey = computed<string>(() => `home.titleBig.${this.environmentService.getPortalCustomerType()}`);
  wrongPortalKeys = computed<{ text?: string; link?: string; text2?: string }>(() => {
    return {
      text: `home.wrongPortal.${this.environmentService.getPortalCustomerType()}.text`,
      link: `home.wrongPortal.${this.environmentService.getPortalCustomerType()}.link`,
      text2: `home.wrongPortal.${this.environmentService.getPortalCustomerType()}.text2`,
    };
  });
  switchPortalUrl = computed<string>(() => this.navigationLogic.getSwitchPortalUrl());

  private router = inject(Router);
  private authService = inject(AuthService);
  private activatedRoute = inject(ActivatedRoute);
  private appStorage = inject(AppStorageService);
  private staticContentService = inject(StaticContentService);
  private profileService = inject(ProfileService);
  private environmentService = inject(EnvironmentService);
  private navigationLogic = inject(NavigationLogic);
  private toastService = inject(ToastService);

  async ngOnInit(): Promise<void> {
    this.content.set(this.defaultContent());
    if (this.profileService.getProfileInfo().toggleSetDesignator === 'LP') {
      await this.profileService.loadProfileInfo();
    }

    this.checkEmailInvitation();
    this.checkForDynamicAlert();
    await this.loadFrontpageData();

    this.appStorage.removeRequestedRoute();
  }

  async register() {
    await this.authService.register();
  }

  login() {
    this.authService.login();
  }

  navigateToCampaignUri() {
    window.location.href = this.content().imageHref;
  }

  private checkEmailInvitation() {
    const guid = this.activatedRoute.snapshot.queryParams['mail'];
    this.isEmailInvitationLandingPage.set(!!guid);
    this.appStorage.setEmailInvitation(this.isEmailInvitationLandingPage());
  }

  private checkForDynamicAlert(): void {
    const { hash } = window.location;
    if (hash.indexOf('logout=success') !== -1) {
      this.toastService.publishNewToast({
        type: 'success',
        title: 'home.logoutSuccess.title',
        message: 'home.logoutSuccess.message',
      });
    } else if (hash.indexOf('logout=automatic') !== -1) {
      this.toastService.publishNewToast({
        type: 'info',
        title: 'home.logoutAutomatic.title',
        message: 'home.logoutAutomatic.message',
      });
    } else if (hash.indexOf('logout=new-terms-2023-canceled') !== -1) {
      this.toastService.publishNewToast({
        type: 'danger',
        title: 'home.logoutLogwienNewTermsCanceled.title',
        message: 'home.logoutLogwienNewTermsCanceled.message',
        staysVisible: true,
      });
    } else if (hash.indexOf('logout=new-terms-2023-approval-error') !== -1) {
      this.toastService.publishNewToast({
        type: 'danger',
        title: 'home.logoutLogwienNewTermsApprovalError.title',
        message: 'home.logoutLogwienNewTermsApprovalError.message',
        staysVisible: true,
      });
    } else if (window.location.href.indexOf('session_=') !== -1) {
      window.location.href = '/privat/meine-produkte';
    }
  }

  private async loadFrontpageData() {
    try {
      const data = await this.requestFrontpageData();
      if (!data) {
        return;
      }
      if (data.content?.isEnabled) {
        this.content.set(data.content);
      }
    } catch (e) {
      // ignore error
    } finally {
      this.isLoaded.set(true);
    }
  }

  private async requestFrontpageData(): Promise<FrontpageData> {
    if (this.router.url.startsWith(`/${LANDING_PAGE_EMAIL_INVITATION_ROUTE}`)) {
      return null;
    }

    try {
      return await this.staticContentService.getFrontpageContent();
    } catch (e) {
      // ignore
      return null;
    }
  }
}
